/**
 * Product Kit Builder — Solo
 * Wizard de kit com múltiplos produtos, variantes e add em lote no cart-drawer.
 */
class ProductKitBuilder {
  constructor(container, config) {
    this.container = container;
    this.config = config;
    this.currentSlide = 0;
    this.selections = new Map();
    this._galleryCache = null;
    this._productJsonCache = new Map();
    this._galleryRequestId = 0;
    this.mobileGalleryQuery = window.matchMedia('(max-width: 989px)');
    this.isAccordion = (config.kitLayout || container.dataset.kitLayout || 'wizard') === 'accordion';
    this.kitComplete = false;

    this.slider = container.querySelector('[data-kit-slider]');
    this.slides = Array.from(this.slider.querySelectorAll('.kit-product-slide'));
    this.prevButton = container.querySelector('[data-kit-prev]');
    this.nextButton = container.querySelector('[data-kit-next]');
    this.addButton = container.querySelector('[data-kit-add]');
    this.currentCounter = container.querySelector('[data-kit-current]');
    this.totalCounter = container.querySelector('[data-kit-total]');

    this.init();
  }

  init() {
    const allSlides = Array.from(this.container.querySelectorAll('.kit-product-slide'));
    allSlides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId, 10);
      const rep = parseInt(slide.dataset.rep || '1', 10);
      const productData = this.config.products.find((p) => p.id === productId);
      const key = `${productId}-${rep}`;
      if (productData) {
        this.selections.set(key, {
          productId,
          variantId: productData.firstAvailableVariantId,
        });
      }
    });

    const defaultSize =
      this.config.kitDefaultSize ||
      (this.config.kitSizes && this.config.kitSizes.length
        ? parseInt(this.config.kitSizes[0], 10)
        : 1);
    this.applyKitSize(defaultSize);
    this.setupEventListeners();
    this.showSlide(0);
    this.updateKitSummary();
  }

  setupEventListeners() {
    this.prevButton?.addEventListener('click', () => this.previousSlide());
    this.nextButton?.addEventListener('click', () => this.nextSlide());
    this.addButton?.addEventListener('click', () => this.addToCart());

    this.container.addEventListener('change', (e) => {
      const radio = e.target.closest('.kit-variant-radio');
      if (radio) this.handleVariantChange(e);
    });

    this.container.addEventListener('keydown', (e) => this.handleKeyboard(e));

    this.mobileGalleryQuery.addEventListener('change', () => {
      const slide = this.slides[this.currentSlide];
      if (slide) this.updateGalleryForCurrentSlide(slide);
    });

    this.container.addEventListener('click', (e) => {
      const nextInner = e.target.closest('[data-kit-next-inner]');
      if (nextInner) {
        e.preventDefault();
        e.stopPropagation();
        this.nextSlide();
        return;
      }

      const trigger = e.target.closest('[data-kit-accordion-trigger]');
      if (!trigger || !this.isAccordion) return;
      const slide = trigger.closest('.kit-product-slide');
      if (!slide || slide.getAttribute('data-hidden') === 'true') return;
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      if (slide.classList.contains('is-open')) {
        this.closeAllAccordions();
      } else {
        this.showSlide(index);
      }
    });

    const sizeButtons = this.container.querySelectorAll('[data-kit-size]');
    sizeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const size = parseInt(btn.dataset.kitSize, 10);
        this.applyKitSize(size);
        sizeButtons.forEach((b) => {
          const pressed = parseInt(b.dataset.kitSize, 10) === size;
          b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
          b.classList.toggle('is-active', pressed);
        });
      });
    });
  }

  getVisibleSlides() {
    return Array.from(this.slider.querySelectorAll('.kit-product-slide:not([data-hidden="true"])'));
  }

  applyKitSize(size) {
    const allSlides = Array.from(this.slider.querySelectorAll('.kit-product-slide'));
    allSlides.forEach((slide) => {
      const rep = parseInt(slide.dataset.rep || '1', 10);
      if (rep > size) {
        slide.style.display = 'none';
        slide.setAttribute('data-hidden', 'true');
      } else {
        slide.style.display = '';
        slide.removeAttribute('data-hidden');
      }
    });

    this.slides = this.getVisibleSlides();
    if (this.totalCounter) this.totalCounter.textContent = this.slides.length;

    const previousIndex = this.currentSlide;
    const maxIndex = Math.max(0, this.slides.length - 1);
    let nextIndex = 0;

    if (this.isAccordion) {
      this.kitComplete = false;
      if (previousIndex < 0) {
        nextIndex = -1;
      } else {
        nextIndex = Math.min(previousIndex, maxIndex);
      }
    } else {
      nextIndex = Math.min(Math.max(previousIndex, 0), maxIndex);
    }

    if (nextIndex < 0) {
      this.closeAllAccordions();
    } else {
      this.showSlide(nextIndex);
    }

    const configuredMode = this.config.discountMode || 'percent';
    const discountPercent = (this.config.kitDiscounts && this.config.kitDiscounts[String(size)]) || 0;
    const discountFixed = (this.config.kitDiscountsFixed && this.config.kitDiscountsFixed[String(size)]) || 0;

    if (configuredMode === 'fixed' && discountFixed > 0) {
      this.config.discountMultiplier = 1.0;
      this.config.discountFixedCents = discountFixed;
      this.config.discountType = 'fixed';
      this.config.activeDiscountPercent = 0;
    } else if (configuredMode === 'percent' && discountPercent > 0) {
      this.config.discountMultiplier = (100 - discountPercent) / 100;
      this.config.discountFixedCents = 0;
      this.config.discountType = 'percent';
      this.config.activeDiscountPercent = discountPercent;
    } else {
      this.config.discountMultiplier = 1;
      this.config.discountFixedCents = 0;
      this.config.discountType = 'none';
      this.config.activeDiscountPercent = 0;
    }

    const visibleSlides = this.getVisibleSlides();
    this.slides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId, 10);
      const rep = parseInt(slide.dataset.rep || '1', 10);
      const key = `${productId}-${rep}`;
      const selection = this.selections.get(key);
      const productData = this.config.products.find((p) => p.id === productId);
      let variant;
      if (selection) {
        variant = productData?.variants.find((v) => v.id === selection.variantId) || productData?.variants[0];
      } else {
        variant = productData?.variants[0];
      }
      if (variant) this.updateProductPrice(slide, variant, visibleSlides.length);
      this.updateAccordionSummary(slide);
    });
    this.updateKitSummary();
  }

  handleVariantChange(event) {
    const radio = event.target;
    if (!radio.checked) return;

    const slide = radio.closest('.kit-product-slide');
    const productIndex = parseInt(slide.dataset.productIndex, 10) - 1;
    const productId = parseInt(slide.dataset.productId, 10);
    const rep = parseInt(slide.dataset.rep || '1', 10);
    const fieldset = radio.closest('fieldset');

    const currentValueSpan = fieldset.querySelector('.current-value');
    if (currentValueSpan) {
      currentValueSpan.textContent = radio.value;
      currentValueSpan.dataset.currentValue = radio.value;
    }

    const selectedOptions = Array.from(slide.querySelectorAll('.current-value')).map(
      (el) => el.dataset.currentValue
    );

    const product = this.config.products[productIndex];
    if (!product) return;

    const variant = product.variants.find(
      (v) => JSON.stringify(v.options) === JSON.stringify(selectedOptions)
    );

    if (!variant) return;

    this.selections.set(`${productId}-${rep}`, { productId, variantId: variant.id });
    this.updateProductImage(slide, variant);
    this.updateProductPrice(slide, variant, this.getVisibleSlides().length);
    this.updateNavigationAvailability(variant);
    this.updateSwatchAvailability(slide, product, selectedOptions);
    this.updateAccordionSummary(slide);
    this.updateKitSummary();
  }

  updateProductImage(slide, variant) {
    const image = slide.querySelector('[data-kit-image]');
    if (image && variant.featured_image) {
      image.src = variant.featured_image;
      image.alt = variant.options.join(' - ');
    }

    const slideProductId = parseInt(slide.dataset.productId, 10);
    const productData = this.config.products.find((p) => p.id === slideProductId);
    if (!productData) return;

    const colorValue = this.getSelectedColor(slide, variant);
    this.updateGalleryWithProductImages(productData.handle, colorValue);
  }

  getSelectedColor(slide, variant) {
    let colorValue = '';
    slide.querySelectorAll('fieldset').forEach((fs) => {
      const handle = (fs.dataset.handle || '').toLowerCase();
      const label = (fs.querySelector('.form__label')?.textContent || '').toLowerCase();
      if (
        handle.includes('cor') ||
        handle.includes('color') ||
        label.includes('cor') ||
        label.includes('color')
      ) {
        colorValue = fs.querySelector('.current-value')?.dataset?.currentValue || '';
      }
    });
    return colorValue || (variant && variant.options[0]) || '';
  }

  updateProductPrice(slide, variant, totalProducts = 1) {
    const priceElement = slide.querySelector('[data-kit-price]');
    if (!priceElement) return;

    let discountedPrice = variant.price;
    let hasDiscount = false;

    if (this.config.discountType === 'fixed' && this.config.discountFixedCents > 0) {
      const discountPerProduct = Math.floor(this.config.discountFixedCents / Math.max(totalProducts, 1));
      discountedPrice = Math.max(0, variant.price - discountPerProduct);
      hasDiscount = discountPerProduct > 0;
    } else if (this.config.discountType === 'percent' && this.config.discountMultiplier < 1) {
      discountedPrice = variant.price * this.config.discountMultiplier;
      hasDiscount = true;
    }

    priceElement.textContent = this.formatMoney(discountedPrice);

    const compareElement = slide.querySelector('[data-kit-compare-price]');
    if (compareElement) {
      if (hasDiscount) {
        compareElement.textContent = this.formatMoney(variant.price);
        compareElement.style.display = '';
      } else {
        compareElement.style.display = 'none';
      }
    }
  }

  updateNavigationAvailability(variant) {
    if (!variant.available) {
      this.nextButton && (this.nextButton.disabled = true);
      if (this.addButton) {
        if (this.isAccordion && !this.kitComplete) {
          this.addButton.disabled = false;
        } else {
          this.addButton.disabled = true;
        }
      }
    } else {
      this.nextButton && (this.nextButton.disabled = false);
      this.addButton && (this.addButton.disabled = false);
    }
  }

  updateSwatchAvailability(slide, product, selectedOptions) {
    const fieldsets = slide.querySelectorAll('fieldset[data-option-index]');
    fieldsets.forEach((fieldset, index) => {
      if (index === 0 && fieldsets.length > 1) return;

      const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
      const previousOption = index > 0 ? selectedOptions[index - 1] : null;
      const relevantVariants = previousOption
        ? product.variants.filter((v) => v.options[index - 1] === previousOption)
        : product.variants;

      radioButtons.forEach((radio) => {
        const label = radio.nextElementSibling;
        const value = radio.value;
        const isAvailable = relevantVariants.some((v) => v.options[index] === value && v.available);

        radio.disabled = !isAvailable;
        radio.classList.toggle('is-disabled', !isAvailable);
        if (label) {
          label.classList.toggle('available', isAvailable);
          label.classList.toggle('soldout', !isAvailable);
        }
      });
    });
  }

  showSlide(index) {
    if (!this.slides.length) return;

    if (this.isAccordion) {
      this.slides.forEach((slide, i) => {
        slide.style.display = '';
        const open = i === index;
        slide.classList.toggle('is-open', open);
        const trigger = slide.querySelector('[data-kit-accordion-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      this.currentSlide = index;
      if (this.currentCounter) this.currentCounter.textContent = index + 1;
      if (this.slides[index]) {
        this.applyDefaultVariantSelection(this.slides[index]);
        this.updateGalleryForCurrentSlide(this.slides[index]);
      }
      this.updateNavigation();
      this.updateKitSummary();
      return;
    }

    this.slides.forEach((slide) => {
      slide.style.display = 'none';
      slide.classList.remove('is-open');
    });

    if (this.slides[index]) {
      this.slides[index].style.display = 'block';
      this.currentSlide = index;
      if (this.currentCounter) this.currentCounter.textContent = index + 1;
      this.updateNavigation();
      this.updateGalleryForCurrentSlide(this.slides[index]);
    }
  }

  closeAllAccordions() {
    if (!this.isAccordion) return;
    this.slides.forEach((slide) => {
      slide.classList.remove('is-open');
      const trigger = slide.querySelector('[data-kit-accordion-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
    this.currentSlide = -1;
    this.updateNavigation();
    this.updateKitSummary();
  }

  applyDefaultVariantSelection(slide) {
    if (!slide) return;
    const productId = parseInt(slide.dataset.productId, 10);
    const rep = parseInt(slide.dataset.rep || '1', 10);
    const productIndex = parseInt(slide.dataset.productIndex, 10) - 1;
    const product = this.config.products[productIndex] || this.config.products.find((p) => p.id === productId);
    if (!product) return;

    const key = `${productId}-${rep}`;
    const selection = this.selections.get(key);
    let variant =
      product.variants.find((v) => v.id === selection?.variantId) ||
      product.variants.find((v) => v.id === product.firstAvailableVariantId) ||
      product.variants.find((v) => v.available) ||
      product.variants[0];
    if (!variant) return;

    const fieldsets = slide.querySelectorAll('fieldset[data-option-index]');
    const selectedOptions = [];

    fieldsets.forEach((fieldset, optIndex) => {
      const desired = variant.options[optIndex];
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      let radio =
        radios.find((r) => r.value === desired && !r.disabled) ||
        radios.find((r) => !r.disabled) ||
        radios[0];
      if (!radio) return;
      radio.checked = true;
      const currentValueSpan = fieldset.querySelector('.current-value');
      if (currentValueSpan) {
        currentValueSpan.textContent = radio.value;
        currentValueSpan.dataset.currentValue = radio.value;
      }
      selectedOptions[optIndex] = radio.value;
    });

    const matched = product.variants.find(
      (v) => JSON.stringify(v.options) === JSON.stringify(selectedOptions)
    );
    if (matched) variant = matched;

    this.selections.set(key, { productId, variantId: variant.id });
    this.updateProductImage(slide, variant);
    this.updateProductPrice(slide, variant, this.getVisibleSlides().length);
    this.updateSwatchAvailability(slide, product, selectedOptions);
    this.updateAccordionSummary(slide);
    this.updateNavigationAvailability(variant);
  }

  updateAccordionSummary(slide) {
    const summary = slide?.querySelector('[data-kit-accordion-summary]');
    if (!summary) return;
    const values = Array.from(slide.querySelectorAll('.current-value'))
      .map((el) => (el.textContent || '').trim())
      .filter(Boolean);
    summary.textContent = values.join(' / ');
  }

  getKitTotals() {
    let total = 0;
    let compare = 0;
    const visible = this.getVisibleSlides();

    for (const slide of visible) {
      const productId = parseInt(slide.dataset.productId, 10);
      const rep = parseInt(slide.dataset.rep || '1', 10);
      const productIndex = parseInt(slide.dataset.productIndex, 10) - 1;
      const product = this.config.products[productIndex] || this.config.products.find((p) => p.id === productId);
      const selection = this.selections.get(`${productId}-${rep}`);
      const variant =
        product?.variants.find((v) => v.id === selection?.variantId) ||
        product?.variants.find((v) => v.id === product.firstAvailableVariantId) ||
        product?.variants[0];
      if (!variant) continue;

      compare += variant.price;

      if (this.config.discountType === 'percent' && this.config.discountMultiplier < 1) {
        total += variant.price * this.config.discountMultiplier;
      } else if (this.config.discountType === 'fixed' && this.config.discountFixedCents > 0) {
        const discountPerProduct = Math.floor(this.config.discountFixedCents / Math.max(visible.length, 1));
        total += Math.max(0, variant.price - discountPerProduct);
      } else {
        total += variant.price;
      }
    }

    return {
      total: Math.round(total),
      compare: Math.round(compare),
    };
  }

  updateKitSummary() {
    const { total, compare } = this.getKitTotals();
    const totalEl = this.container.querySelector('[data-kit-total-price]');
    const savingsWrap = this.container.querySelector('[data-kit-savings]');
    const savingsAmt = this.container.querySelector('[data-kit-savings-amount]');

    if (totalEl) {
      totalEl.textContent = this.formatMoney(total);
    }

    const saved = compare - total;

    if (savingsWrap && savingsAmt && saved > 0) {
      savingsAmt.textContent = this.formatMoney(saved);
      savingsWrap.style.display = '';
    } else if (savingsWrap) {
      savingsWrap.style.display = 'none';
    }

    this.updateMainProductPrice(total, compare);
  }

  updateMainProductPrice(totalCents, compareCents) {
    const priceRoot = document.querySelector('.thb-product-detail .product-price-container .price');
    if (!priceRoot) return;

    const hasDiscount = compareCents > totalCents;
    const totalText = this.formatMoneyDisplay(totalCents);
    const compareText = this.formatMoneyDisplay(compareCents);
    const unitPrice = priceRoot.querySelector('.unit-price');
    const badges = priceRoot.querySelector('.badges');

    for (const node of Array.from(priceRoot.querySelectorAll(':scope > del, :scope > ins'))) {
      node.remove();
    }

    const fragment = document.createDocumentFragment();

    if (hasDiscount) {
      const del = document.createElement('del');
      const delAmount = document.createElement('span');
      delAmount.className = 'amount';
      delAmount.textContent = compareText;
      del.appendChild(delAmount);
      fragment.appendChild(del);

      const ins = document.createElement('ins');
      const insAmount = document.createElement('span');
      insAmount.className = 'amount discounted';
      insAmount.textContent = totalText;
      ins.appendChild(insAmount);
      fragment.appendChild(ins);
    } else {
      const ins = document.createElement('ins');
      const insAmount = document.createElement('span');
      insAmount.className = 'amount';
      insAmount.textContent = totalText;
      ins.appendChild(insAmount);
      fragment.appendChild(ins);
    }

    const insertBefore = unitPrice || badges || null;
    priceRoot.insertBefore(fragment, insertBefore);

    if (unitPrice) {
      unitPrice.classList.add('hidden');
    }
  }

  updateGalleryForCurrentSlide(slide) {
    const productId = parseInt(slide.dataset.productId, 10);
    const productData = this.config.products.find((p) => p.id === productId);
    if (!productData) return;
    const selectedColor = this.getSelectedColor(slide, null);
    this.updateGalleryWithProductImages(productData.handle, selectedColor);
  }

  previousSlide() {
    if (this.isAccordion && this.currentSlide < 0) return;
    if (this.currentSlide > 0) this.showSlide(this.currentSlide - 1);
  }

  nextSlide() {
    if (this.isAccordion && this.currentSlide < 0) {
      this.showSlide(0);
      return;
    }
    if (this.currentSlide < this.slides.length - 1) {
      this.showSlide(this.currentSlide + 1);
      return;
    }
    if (this.isAccordion) {
      this.kitComplete = true;
      this.closeAllAccordions();
    }
  }

  updateNavigation() {
    if (this.isAccordion) {
      if (this.nextButton) this.nextButton.style.display = 'none';
      if (this.prevButton) this.prevButton.style.display = 'none';
      if (this.addButton) {
        const labelEl = this.addButton.querySelector('.single-add-to-cart-button--text');
        const readyLabel = this.addButton.dataset.kitAddLabel || 'Adicionar ao carrinho';
        const pendingLabel = this.addButton.dataset.kitPendingLabel || 'Selecione as peças';
        this.addButton.style.display = 'flex';
        this.addButton.classList.toggle('is-ready', this.kitComplete);
        this.addButton.classList.toggle('kit-add-button--pending', !this.kitComplete);
        this.addButton.setAttribute('aria-disabled', this.kitComplete ? 'false' : 'true');
        this.addButton.setAttribute('aria-label', this.kitComplete ? readyLabel : pendingLabel);
        if (labelEl) labelEl.textContent = this.kitComplete ? readyLabel : pendingLabel;
        this.addButton.disabled = false;
      }
      this.slides.forEach((slide) => {
        const innerNext = slide.querySelector('[data-kit-next-inner]');
        if (innerNext) innerNext.hidden = false;
      });
      return;
    }

    if (this.prevButton) this.prevButton.disabled = this.currentSlide === 0;
    const isLastSlide = this.currentSlide === this.slides.length - 1;
    if (isLastSlide) {
      if (this.nextButton) this.nextButton.style.display = 'none';
      if (this.addButton) this.addButton.style.display = 'flex';
    } else {
      if (this.nextButton) this.nextButton.style.display = 'flex';
      if (this.addButton) this.addButton.style.display = 'none';
    }
  }

  handleKeyboard(event) {
    switch (event.key) {
      case 'ArrowLeft':
        if (this.currentSlide > 0) {
          event.preventDefault();
          this.previousSlide();
        }
        break;
      case 'ArrowRight':
        if (this.currentSlide < this.slides.length - 1) {
          event.preventDefault();
          this.nextSlide();
        }
        break;
    }
  }

  buildDiscountTiersProperty() {
    const productsCount = (this.config.products && this.config.products.length) || 1;
    const sizes = this.config.kitSizes || [];
    const discounts = this.config.kitDiscounts || {};
    const parts = [];

    sizes.forEach((sizeStr) => {
      const size = parseInt(sizeStr, 10);
      if (!size || size < 1) return;
      const pieces = productsCount * size;
      const pct = parseInt(discounts[String(size)], 10) || 0;
      if (pieces > 0 && pct > 0) {
        parts.push(`${pieces}:${pct}`);
      }
    });

    return parts.join(',');
  }

  async addToCart() {
    if (!this.addButton) return;
    if (this.isAccordion && !this.kitComplete) return;
    this.addButton.disabled = true;
    this.addButton.classList.add('loading');
    this.addButton.setAttribute('aria-busy', 'true');

    const visibleSlides = this.getVisibleSlides();
    const items = [];
    const kitId = `kit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let kitLine = 0;

    visibleSlides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId, 10);
      const rep = parseInt(slide.dataset.rep || '1', 10);
      const selection = this.selections.get(`${productId}-${rep}`);
      if (selection && selection.variantId) {
        kitLine += 1;
        items.push({
          id: selection.variantId,
          quantity: 1,
          properties: {
            _kit_id: kitId,
            _kit_line: String(kitLine),
            _kit_label: this.config.kitProductTitle || this.config.kitHeading || 'Conjunto',
            _kit_closed: this.config.closedKit ? 'true' : 'false',
            _kit_discount_percent:
              this.config.activeDiscountPercent > 0
                ? String(this.config.activeDiscountPercent)
                : '',
            _kit_discount_tiers: this.buildDiscountTiersProperty(),
          },
        });
      }
    });

    if (!items.length) {
      this.resetAddButton();
      return;
    }

    const root = window.theme?.routes?.root_url || window.Shopify?.routes?.root || '/';
    const cartAddUrl = window.theme?.routes?.cart_add_url || `${root}cart/add.js`;

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: 'cart-drawer,cart-bubble,main-cart',
          sections_url: window.location.pathname,
        }),
      });

      const responseJson = await response.json().catch(() => ({}));

      if (response.ok && !responseJson.status) {
        this.renderCartSections(responseJson);
        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart:item-added', {
            product: responseJson.items?.[0],
          });
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else if (typeof drawer?.refresh === 'function') {
          drawer.refresh();
          document.body.classList.add('open-cc');
          drawer.classList.add('active');
        }
      } else {
        const message =
          responseJson.description || responseJson.message || 'Erro ao adicionar ao carrinho.';
        window.alert(message);
      }
    } catch (error) {
      window.alert('Erro ao adicionar produtos ao carrinho. Tente novamente.');
    } finally {
      this.resetAddButton();
    }
  }

  resetAddButton() {
    if (!this.addButton) return;
    this.addButton.disabled = false;
    this.addButton.classList.remove('loading');
    this.addButton.removeAttribute('aria-busy');
  }

  renderCartSections(parsedState) {
    if (!parsedState?.sections) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.refresh === 'function') drawer.refresh();
      return;
    }

    const map = [
      { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
      { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' },
      { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    ];

    map.forEach(({ id, section, selector }) => {
      const html = parsedState.sections[section];
      if (!html) return;
      const host = document.getElementById(id);
      if (!host) return;
      const target = host.querySelector(selector) || host;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector(selector);
      if (source) target.innerHTML = source.innerHTML;
    });

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.removeProductEvent === 'function') {
      drawer.removeProductEvent();
    }
  }

  getGalleryRoot() {
    return (
      document.querySelector('.product-gallery-unbound-grid') ||
      document.querySelector('[id^="MediaGallery-"]') ||
      null
    );
  }

  normalizeColor(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  getColorNeedles(colorValue) {
    const names = window.ColorAssimilation ? window.ColorAssimilation.expand(colorValue) : [colorValue];
    return [...new Set(names.map((name) => this.normalizeColor(name)).filter(Boolean))];
  }

  altMatchesColor(alt, needles) {
    return Boolean(alt) && needles.some((needle) => alt.includes(needle) || needle.includes(alt));
  }

  async updateGalleryWithProductImages(productHandle, colorValue) {
    if (!productHandle) return;

    const pageHandle = this.config.pageProductHandle;
    const sameProduct = !pageHandle || pageHandle === productHandle;
    const requestId = ++this._galleryRequestId;
    const isCurrent = () => requestId === this._galleryRequestId;
    const isMobile = this.mobileGalleryQuery.matches;
    const gallery = isMobile ? null : this.getGalleryRoot();

    if (sameProduct) {
      if (isMobile) this.filterMobileSliderByColor(colorValue);
      else if (gallery) this.filterUnboundGalleryByColor(gallery, colorValue);
      return;
    }

    const productJson = await this.fetchProductJson(productHandle);
    if (!productJson || !isCurrent()) return;

    const medias = this.getColorMedia(productJson, colorValue);
    if (!medias.length) return;

    if (isMobile) await this.applyForeignMediaToMobileSlider(productJson, medias, isCurrent);
    else if (gallery) await this.applyForeignProductMedia(gallery, productJson, medias, isCurrent);
  }

  waitForImage(img, timeout = 2000) {
    if (!img || (img.complete && img.naturalWidth)) return Promise.resolve();

    const loaded =
      typeof img.decode === 'function'
        ? img.decode().catch(() => {})
        : new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          });
    return Promise.race([loaded, new Promise((resolve) => setTimeout(resolve, timeout))]);
  }

  async fetchProductJson(handle) {
    if (this._productJsonCache.has(handle)) return this._productJsonCache.get(handle);

    try {
      const root = window.theme?.routes?.root_url || window.Shopify?.routes?.root || '/';
      const base = root.endsWith('/') ? root : `${root}/`;
      const response = await fetch(`${base}products/${handle}.js`);
      if (!response.ok) return null;
      const productJson = await response.json();
      this._productJsonCache.set(handle, productJson);
      return productJson;
    } catch (error) {
      return null;
    }
  }

  getColorMedia(productJson, colorValue) {
    const source = productJson.media?.length ? productJson.media : productJson.images || [];
    const medias = source
      .map((media) => (typeof media === 'string' ? { src: media } : media))
      .filter((media) => media && (!media.media_type || media.media_type === 'image') && (media.src || media.preview_image?.src));

    const needles = this.getColorNeedles(colorValue);
    if (!needles.length) return medias;

    const slugs = needles.map((needle) => needle.replace(/\s+/g, '-'));
    const byColor = medias.filter((media) => {
      const alt = this.normalizeColor(media.alt || '');
      const src = this.normalizeColor(media.src || media.preview_image?.src || '');
      return needles.some((needle) => alt.includes(needle)) || slugs.some((slug) => src.includes(slug));
    });
    return byColor.length ? byColor : medias;
  }

  resizeImageUrl(src, width) {
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  getMobileSlider() {
    return document.querySelector('.thb-product-detail .mobile-only product-slider');
  }

  rememberMobileSlideState(slides) {
    for (const slide of slides) {
      if (!slide.dataset.kitOriginalActive) {
        slide.dataset.kitOriginalActive = slide.classList.contains('is-active') ? 'true' : 'false';
      }
    }
  }

  restoreMobileSlideState(slides) {
    for (const slide of slides) {
      if (slide.dataset.kitOriginalActive) {
        slide.classList.toggle('is-active', slide.dataset.kitOriginalActive === 'true');
      }
    }
  }

  setMobileThumbnailsHidden(slider, hidden) {
    const thumbnails = slider.closest('.product-gallery-container')?.querySelector('scroll-shadow');
    if (thumbnails) thumbnails.style.display = hidden ? 'none' : '';
  }

  refreshMobileSlider(slider) {
    if (typeof slider.reInit !== 'function' || !slider.flkty) return;
    slider.reInit();
    slider.flkty?.select(0, false, true);
  }

  filterMobileSliderByColor(colorValue) {
    const slider = this.getMobileSlider();
    if (!slider) return;

    for (const kitSlide of slider.querySelectorAll('[data-kit-slide]')) kitSlide.remove();
    this.setMobileThumbnailsHidden(slider, false);

    const slides = Array.from(slider.querySelectorAll('.product-images__slide'));
    if (!slides.length) return;
    this.rememberMobileSlideState(slides);

    const needles = this.getColorNeedles(colorValue);
    let matched = 0;

    if (needles.length) {
      for (const slide of slides) {
        const img = slide.querySelector('.product-single__media-image img');
        if (!img) {
          slide.classList.toggle('is-active', slide.dataset.kitOriginalActive === 'true');
          continue;
        }
        const hit = this.altMatchesColor(this.normalizeColor(img.getAttribute('alt')), needles);
        slide.classList.toggle('is-active', hit);
        if (!hit) continue;
        matched += 1;
        if (img.classList.contains('lazyload')) window.lazySizes?.loader?.unveil(img);
      }
    }

    if (!matched) this.restoreMobileSlideState(slides);
    this.refreshMobileSlider(slider);
  }

  async applyForeignMediaToMobileSlider(productJson, medias, isCurrent) {
    const slider = this.getMobileSlider();
    if (!slider) return;

    const originals = Array.from(slider.querySelectorAll('.product-images__slide:not([data-kit-slide])'));
    const template = originals.find((slide) => slide.querySelector('.product-single__media-image img'));
    if (!template) return;

    const slides = medias
      .map((media, index) => this.buildMobileKitSlide(template, media, productJson.title, index === 0))
      .filter(Boolean);
    if (!slides.length) return;

    await this.waitForImage(slides[0].querySelector('.product-single__media-image img'));
    if (!isCurrent()) return;

    this.rememberMobileSlideState(originals);
    for (const slide of originals) slide.classList.remove('is-active');
    for (const kitSlide of slider.querySelectorAll('[data-kit-slide]')) kitSlide.remove();

    slider.append(...slides);
    this.setMobileThumbnailsHidden(slider, true);
    this.refreshMobileSlider(slider);
    this.bindMobileKitZoom(slider);
  }

  buildMobileKitSlide(template, media, fallbackAlt, isFirst = false) {
    const src = media.src || media.preview_image?.src;
    if (!src) return null;

    const slide = template.cloneNode(true);
    for (const attr of ['id', 'style', 'aria-hidden', 'data-media-id', 'data-set-name', 'data-group', 'data-kit-original-active']) {
      slide.removeAttribute(attr);
    }
    slide.classList.remove('is-selected', 'is-initial-selected', 'product-images__slide-item--variant');
    slide.classList.add('is-active');
    slide.dataset.kitSlide = 'true';
    slide.querySelector('.figcaption')?.remove();

    const width = media.width || media.preview_image?.width;
    const height = media.height || media.preview_image?.height;
    const ratio = media.aspect_ratio || (width && height ? width / height : null);

    const wrapper = slide.querySelector('.product-single__media-image');
    wrapper.removeAttribute('id');
    if (ratio && wrapper.classList.contains('aspect-ratio--adapt')) {
      wrapper.style.setProperty('--padding-bottom', `${(100 / ratio).toFixed(2)}%`);
    }

    const templateImg = wrapper.querySelector('img');
    const img = document.createElement('img');
    const lazyClasses = ['lazyload', 'lazyloading', 'lazyautosizes', 'ls-is-cached'];
    img.className = [...templateImg.classList].filter((name) => !lazyClasses.includes(name)).join(' ');
    img.classList.add('lazyloaded');
    img.alt = media.alt || fallbackAlt || '';
    if (width && height) {
      img.width = width;
      img.height = height;
    }
    img.decoding = 'async';
    img.fetchPriority = isFirst ? 'high' : 'low';
    img.sizes = '(max-width: 767px) 75vw, 100vw';
    img.srcset = [375, 750, 1100, 1500].map((size) => `${this.resizeImageUrl(src, size)} ${size}w`).join(', ');
    img.src = this.resizeImageUrl(src, 800);
    templateImg.replaceWith(img);

    const zoom = wrapper.querySelector('.product-single__media-zoom');
    if (zoom) {
      zoom.href = this.resizeImageUrl(src, 2000);
      zoom.dataset.msrc = this.resizeImageUrl(src, 20);
      if (width && height) {
        zoom.dataset.w = width;
        zoom.dataset.h = height;
      }
      zoom.removeAttribute('data-index');
    }

    return slide;
  }

  bindMobileKitZoom(slider) {
    if (typeof slider.zoomClick !== 'function') return;
    for (const link of slider.querySelectorAll('[data-kit-slide] .product-single__media-zoom')) {
      link.addEventListener('click', (event) => slider.zoomClick(event, link));
    }
  }

  filterUnboundGalleryByColor(gallery, colorValue) {
    const items = gallery.querySelectorAll('.product-gallery-unbound-item');
    if (!items.length) return;

    const needles = this.getColorNeedles(colorValue);
    if (!needles.length) {
      items.forEach((item) => {
        item.style.display = '';
      });
      return;
    }

    let matched = 0;
    items.forEach((item) => {
      const img = item.querySelector('img.unbound-zoom-thumb, img');
      if (!img) {
        item.style.display = '';
        return;
      }
      const alt = this.normalizeColor(img.getAttribute('alt') || '');
      const hit = this.altMatchesColor(alt, needles);
      item.style.display = hit ? '' : 'none';
      if (hit) matched += 1;
    });

    if (!matched) {
      items.forEach((item) => {
        item.style.display = '';
      });
    }

    document.dispatchEvent(new CustomEvent('unbound:gallery-filtered', { bubbles: true }));
  }

  async applyForeignProductMedia(gallery, productJson, filtered, isCurrent) {
    const items = gallery.querySelectorAll('.product-gallery-unbound-item');
    const visibleCount = Math.min(items.length, filtered.length);

    await Promise.all(
      filtered.slice(0, visibleCount).map((media) => {
        const preload = new Image();
        preload.src = media.src || media.preview_image?.src;
        return this.waitForImage(preload);
      })
    );
    if (!isCurrent()) return;

    if (!this._galleryCache) {
      this._galleryCache = gallery.innerHTML;
    }

    items.forEach((item, index) => {
      const media = filtered[index] || filtered[0];
      const img = item.querySelector('img.unbound-zoom-thumb, img');
      if (!media || !img) {
        item.style.display = index < filtered.length ? '' : 'none';
        return;
      }
      const src = media.src || media.preview_image?.src;
      if (src) {
        img.src = src;
        img.srcset = '';
        img.alt = media.alt || productJson.title || '';
      }
      item.style.display = index < filtered.length ? '' : 'none';
    });

    document.dispatchEvent(new CustomEvent('unbound:gallery-filtered', { bubbles: true }));
  }

  formatMoney(cents) {
    const format = this.config.moneyFormat || window.theme?.settings?.money_format;
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, format || '{{amount}}');
    }
    const value = (Number(cents) / 100).toFixed(2).replace('.', ',');
    return `R$ ${value}`;
  }

  formatMoneyDisplay(cents) {
    return String(this.formatMoney(cents)).split(',')[0];
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-kit-id]').forEach((container) => {
    const id = container.dataset.kitId;
    const config = window.ProductKitBuilder && window.ProductKitBuilder[id];
    if (config && !container.dataset.kitReady) {
      container.dataset.kitReady = 'true';
      new ProductKitBuilder(container, config);
    }
  });
});

document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('[data-kit-id]:not([data-kit-ready])').forEach((container) => {
    const id = container.dataset.kitId;
    const config = window.ProductKitBuilder && window.ProductKitBuilder[id];
    if (config) {
      container.dataset.kitReady = 'true';
      new ProductKitBuilder(container, config);
    }
  });
});
